// User-selectable accent palette — pure, React-free half.
//
// Same shape as src/viewer/state/viewerSettings.ts: pure localStorage-backed
// load/save helpers with no React dependency, so they can be unit-tested in
// isolation and called from the boot path (src/main.tsx) before anything
// mounts. The React half lives in src/ui/useAccent.ts.
//
// HOW A PALETTE SWITCH WORKS
// --------------------------
// The accent is ONE token scale (`--accent`, `--accent-hover`, … see
// src/index.css). A palette is a `[data-accent="<id>"]` block that overrides
// only that scale. So switching is a single attribute write on <html>, and
// every consumer — including the ~150 call sites still written against the
// `--brand-*` aliases — follows for free. Nothing here is per-component.
//
// The WebGL layers are the one exception: a shader uniform and a three.js
// material colour cannot read a CSS variable, so `ACCENT_PALETTES[].accent`
// carries the same hex the stylesheet does and `useAccentColor()` feeds it to
// them. The unit test pins the two copies together.

export type AccentId = 'amber' | 'cobalt' | 'cyan' | 'violet' | 'rose' | 'slate'

export interface AccentPalette {
  id: AccentId
  /** Human label under the swatch in Settings. */
  name: string
  /** `--accent` — also what the WebGL layers paint with. */
  accent: string
  /** `--accent-hover` — the swatch's second band. */
  hover: string
  /** A 30% wash of the accent — the swatch's third band ONLY (a preview of
   *  `--accent-subtle`, lifted so it is visible at swatch size; the real
   *  `--accent-subtle` at 12-14% is invisible in a 34px chip). */
  subtle: string
}

/**
 * The six shipped palettes, in the founder-signed order (Amber first — it is
 * today's accent, so nothing changes for an existing user who never opens
 * Settings). Values come from .verify/accent-palettes-design.html; they must
 * stay identical to the `[data-accent="…"]` blocks in src/index.css.
 */
export const ACCENT_PALETTES: readonly AccentPalette[] = [
  { id: 'amber', name: 'Amber', accent: '#FF7A45', hover: '#FF9366', subtle: 'rgba(255, 122, 69, 0.3)' },
  { id: 'cobalt', name: 'Cobalt', accent: '#4C8DF5', hover: '#6BA3FF', subtle: 'rgba(76, 141, 245, 0.3)' },
  { id: 'cyan', name: 'Cyan', accent: '#25AEC9', hover: '#3EC6E0', subtle: 'rgba(37, 174, 201, 0.3)' },
  { id: 'violet', name: 'Violet', accent: '#9A7CF0', hover: '#B199FF', subtle: 'rgba(154, 124, 240, 0.3)' },
  { id: 'rose', name: 'Rose', accent: '#E063B0', hover: '#F07DC4', subtle: 'rgba(224, 99, 176, 0.3)' },
  { id: 'slate', name: 'Slate', accent: '#8CA0B8', hover: '#A6B8CE', subtle: 'rgba(140, 160, 184, 0.3)' },
] as const

/** Amber is the accent the app has always shipped — never change this default
 *  without a migration, or every existing user silently gets re-themed. */
export const DEFAULT_ACCENT: AccentId = 'amber'

export const ACCENT_KEY = 'orovia.ui.accent'

/** The attribute the palette blocks in src/index.css select on. */
export const ACCENT_ATTRIBUTE = 'data-accent'

/**
 * Fired on `window` after the accent changes, so every mounted consumer
 * (Settings, the WebGL layers) resyncs from one write. Needed because the
 * preference is app-global chrome with no owning provider — an event keeps it
 * to ONE source of truth (localStorage + the attribute) instead of a second,
 * parallel store.
 */
export const ACCENT_CHANGE_EVENT = 'orovia:accentchange'

export function isAccentId(value: unknown): value is AccentId {
  return ACCENT_PALETTES.some((p) => p.id === value)
}

/** The palette record for `id`, falling back to the default for anything else. */
export function accentPalette(id: AccentId): AccentPalette {
  return ACCENT_PALETTES.find((p) => p.id === id) ?? ACCENT_PALETTES[0]
}

export function loadAccent(): AccentId {
  if (typeof window === 'undefined') return DEFAULT_ACCENT
  try {
    const stored = window.localStorage.getItem(ACCENT_KEY)
    return isAccentId(stored) ? stored : DEFAULT_ACCENT
  } catch {
    // Storage blocked (private mode) — non-fatal, fall back to the default.
    return DEFAULT_ACCENT
  }
}

export function saveAccent(id: AccentId): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(ACCENT_KEY, id)
  } catch {
    /* storage blocked — non-fatal, the choice just won't survive a reload */
  }
}

/**
 * Put `id` on <html>, persist it, and notify every mounted consumer.
 *
 * Writing the attribute is what actually recolours the app; the persist and the
 * event are the two things that must happen with it, which is why this is one
 * function rather than three call sites that can drift.
 */
export function applyAccent(id: AccentId): void {
  if (typeof document === 'undefined') return
  const next = isAccentId(id) ? id : DEFAULT_ACCENT
  document.documentElement.setAttribute(ACCENT_ATTRIBUTE, next)
  saveAccent(next)
  window.dispatchEvent(new CustomEvent(ACCENT_CHANGE_EVENT, { detail: next }))
}

/**
 * Boot hook: apply the stored choice before the first render so a non-default
 * palette never flashes amber. Called once from src/main.tsx.
 */
export function initAccent(): AccentId {
  const id = loadAccent()
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute(ACCENT_ATTRIBUTE, id)
  }
  return id
}
